import { useMemo } from 'react';
import { Activity, Boxes, Building2, PackageCheck, Sparkles, Target, TrendingUp, WalletCards } from 'lucide-react';
import type { EChartsCoreOption } from 'echarts/core';
import Chart from '../components/Chart';
import MetricCard from '../components/MetricCard';
import ProgressCard from '../components/ProgressCard';
import { buildInsights, buildTrend, categoryTotals, targetForPeriod } from '../lib/analytics';
import { formatCompactCurrency, formatNumber, formatPercent } from '../lib/format';
import type { PeriodMode, SalesRecord, SummaryMetrics, TargetConfig } from '../types';

interface OverviewViewProps {
  records: SalesRecord[];
  summary: SummaryMetrics;
  targets: TargetConfig;
  periodMode: PeriodMode;
  onPeriodMode: (mode: PeriodMode) => void;
  onOpenTargets: () => void;
}

const chartText = { color: '#7d899d', fontFamily: 'Inter, Microsoft YaHei, sans-serif' };

export default function OverviewView({
  records,
  summary,
  targets,
  periodMode,
  onPeriodMode,
  onOpenTargets,
}: OverviewViewProps) {
  const trend = useMemo(() => buildTrend(records, targets.targetProduct), [records, targets.targetProduct]);
  const brands = useMemo(() => categoryTotals(records, 'brand'), [records]);
  const insights = useMemo(() => buildInsights(records, summary, targets), [records, summary, targets]);
  const salesTarget = targetForPeriod(targets.annualSalesTarget, periodMode);
  const productTarget = targetForPeriod(targets.annualProductQuantityTarget, periodMode);

  const salesOption = useMemo<EChartsCoreOption>(() => ({
    animationDuration: 700,
    color: ['#2563eb', '#16a394'],
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#172033',
      borderWidth: 0,
      textStyle: { color: '#fff' },
      valueFormatter: (value: unknown) => formatCompactCurrency(Number(value)),
    },
    grid: { left: 18, right: 18, top: 30, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: trend.map((item) => item.label),
      boundaryGap: false,
      axisLine: { lineStyle: { color: '#e8edf5' } },
      axisTick: { show: false },
      axisLabel: chartText,
    },
    yAxis: {
      type: 'value',
      axisLabel: { ...chartText, formatter: (value: number) => `${Math.round(value / 10000)}万` },
      splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } },
    },
    series: [{
      name: '销售额',
      type: 'line',
      smooth: 0.35,
      symbol: 'circle',
      symbolSize: 7,
      showSymbol: trend.length < 18,
      lineStyle: { width: 3 },
      itemStyle: { borderWidth: 2, borderColor: '#fff' },
      areaStyle: {
        opacity: 0.16,
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: '#2563eb' }, { offset: 1, color: 'rgba(37,99,235,0)' }],
        },
      },
      data: trend.map((item) => Number(item.sales.toFixed(2))),
    }],
  }), [trend]);

  const brandOption = useMemo<EChartsCoreOption>(() => ({
    animationDuration: 700,
    color: ['#2563eb', '#19a596', '#7868e6', '#f59e0b', '#ee6f72'],
    tooltip: {
      trigger: 'item',
      backgroundColor: '#172033',
      borderWidth: 0,
      textStyle: { color: '#fff' },
      formatter: (params: { name: string; value: number; percent: number }) =>
        `${params.name}<br/>${formatCompactCurrency(params.value)} · ${params.percent.toFixed(1)}%`,
    },
    legend: {
      bottom: 0,
      left: 'center',
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      textStyle: chartText,
    },
    series: [{
      type: 'pie',
      radius: ['55%', '78%'],
      center: ['50%', '43%'],
      padAngle: 3,
      itemStyle: { borderRadius: 8 },
      label: { show: false },
      emphasis: { scaleSize: 5 },
      data: brands.map((item) => ({ name: item.name, value: Number(item.sales.toFixed(2)) })),
    }],
  }), [brands]);

  return (
    <div className="view-stack">
      <section className="metric-grid">
        <MetricCard label="净销售额" value={formatCompactCurrency(summary.sales)} icon={<WalletCards size={19} />} current={summary.sales} prior={summary.priorSales} note="较上一等长周期" accent="blue" />
        <MetricCard label="销售数量" value={formatNumber(summary.quantity)} icon={<Boxes size={19} />} current={summary.quantity} prior={summary.priorQuantity} note="较上一等长周期" accent="teal" />
        <MetricCard label="活跃终端" value={`${formatNumber(summary.terminals)} 家`} icon={<Building2 size={19} />} current={summary.terminals} prior={summary.priorTerminals} note="较上一等长周期" accent="violet" />
        <MetricCard label="重点单品渗透率" value={formatPercent(summary.penetration)} icon={<PackageCheck size={19} />} current={summary.penetration} prior={summary.priorPenetration} note="终端覆盖口径" accent="amber" />
      </section>

      <section className="section-block">
        <div className="section-heading heading-row">
          <div>
            <span className="eyebrow">目标达成</span>
            <h2>三个核心 KPI</h2>
            <p>金额与数量按年度目标自动折算，终端目标保持固定口径。</p>
          </div>
          <div className="heading-actions">
            <div className="segment-control" aria-label="KPI周期">
              {(['month', 'quarter', 'year'] as PeriodMode[]).map((mode) => (
                <button key={mode} type="button" className={periodMode === mode ? 'active' : ''} onClick={() => onPeriodMode(mode)}>
                  {{ month: '月度', quarter: '季度', year: '年度' }[mode]}
                </button>
              ))}
            </div>
            <button className="text-button" type="button" onClick={onOpenTargets}>调整目标</button>
          </div>
        </div>
        <div className="progress-grid">
          <ProgressCard
            eyebrow={`${{ month: '月度', quarter: '季度', year: '年度' }[periodMode]} KPI 01`}
            title="总销售额达成"
            actual={formatCompactCurrency(summary.sales)}
            target={formatCompactCurrency(salesTarget)}
            progress={salesTarget ? (summary.sales / salesTarget) * 100 : 0}
            icon={<TrendingUp size={18} />}
            color="#2563eb"
          />
          <ProgressCard
            eyebrow={`${{ month: '月度', quarter: '季度', year: '年度' }[periodMode]} KPI 02`}
            title="重点单品数量达成"
            actual={`${formatNumber(summary.targetProductQuantity)} 盒`}
            target={`${formatNumber(productTarget)} 盒`}
            progress={productTarget ? (summary.targetProductQuantity / productTarget) * 100 : 0}
            icon={<Target size={18} />}
            color="#17a394"
            hint={targets.targetProduct}
          />
          <ProgressCard
            eyebrow={`${{ month: '月度', quarter: '季度', year: '年度' }[periodMode]} KPI 03`}
            title="活跃终端达成"
            actual={`${formatNumber(summary.terminals)} 家`}
            target={`${formatNumber(targets.activeTerminalTarget)} 家`}
            progress={targets.activeTerminalTarget ? (summary.terminals / targets.activeTerminalTarget) * 100 : 0}
            icon={<Activity size={18} />}
            color="#7868e6"
          />
        </div>
      </section>

      <section className="dashboard-grid dashboard-grid-wide">
        <article className="panel panel-wide">
          <div className="panel-head">
            <div><span className="eyebrow">销售走势</span><h3>销售额趋势</h3></div>
            <span className="panel-badge">净额口径</span>
          </div>
          <Chart option={salesOption} className="chart-tall" ariaLabel="当前筛选范围内的销售额趋势图" />
        </article>
        <article className="panel">
          <div className="panel-head">
            <div><span className="eyebrow">品牌结构</span><h3>销售贡献占比</h3></div>
          </div>
          <Chart option={brandOption} className="chart-tall" ariaLabel="不同品牌销售额占比环形图" />
        </article>
      </section>

      <section className="insight-panel">
        <div className="insight-intro">
          <span className="ai-mark"><Sparkles size={18} /> 智能经营简报</span>
          <h2>从数据里直接读出行动重点</h2>
          <p>本地分析引擎基于环比、贡献度、渗透缺口和退货信号生成；不发送原始明细。</p>
        </div>
        <div className="insight-list">
          {insights.map((insight, index) => (
            <article className={`insight-item insight-${insight.tone}`} key={`${insight.title}-${index}`}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><strong>{insight.title}</strong><p>{insight.detail}</p></div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
